import { EventBus, WebsocketClient, WebsocketConnectionState } from '@dark-portal/react-infrastructure';
import { create } from 'zustand';

import { ENV } from '@/src/.env';
import { Requester } from '@/src/features/api/_infrastructure/requester';

export interface WebsocketStore {
  connectionState: WebsocketConnectionState;
  eventBus: EventBus;
  connect: () => Promise<void>;
  disconnect: () => void;
}

const eventBus = new EventBus();
const TOKEN_REFRESH_INTERVAL = 60 * 60 * 1000; // 1h

let client: WebsocketClient | null = null;
let token: string | null = null;
let refreshInterval: ReturnType<typeof setInterval> | null = null;
let connecting = false;

async function refreshToken(): Promise<boolean> {
  try {
    const { data } = await Requester.get<undefined, { token: string }>('notifications/ws-token').request;
    token = data.token;
    console.log('[WS] Token acquired');
    return true;
  } catch (error) {
    console.error('[WS] Failed to get token:', error);
    return false;
  }
}

function cleanup() {
  if (refreshInterval) {
    clearInterval(refreshInterval);
    refreshInterval = null;
  }
  client = null;
  token = null;
  connecting = false;
}

export const useWebsocketStore = create<WebsocketStore>((set) => ({
  connectionState: 'disconnected',
  eventBus,

  connect: async () => {
    if (client || connecting) return;
    connecting = true;

    if (!(await refreshToken())) {
      connecting = false;
      return;
    }

    client = new WebsocketClient({
      url: ENV.WS_URL,
      namespace: 'notifications',
      getToken: () => token,
      eventBus,
    });

    client.onStateChange((connectionState) => {
      set({ connectionState });

      if (connectionState === 'disconnected' && !client) {
        cleanup();
      }
    });

    client.connect();
    connecting = false;
    refreshInterval = setInterval(refreshToken, TOKEN_REFRESH_INTERVAL);
  },

  disconnect: () => {
    client?.disconnect();
    cleanup();
    set({ connectionState: 'disconnected' });
  },
}));
